import { environment } from "../../../../environments/environment";
import {
  preserveItems,
  restorePreserved,
} from "../../util/local-storage-preserve.service";

/**
 * Login pelo Keycloak (Authorization Code + PKCE), atras de uma flag. Sem dependencia nova: so
 * fetch/crypto do navegador, no mesmo formato do KeycloakGateway do nuvemenvio-front.
 *
 * Funcoes puras, sem servico Angular: o interceptor (que nao pode depender de $http) e o provider
 * de login usam as mesmas funcoes. O JWT fica em ls.token (angular-local-storage, prefixo "ls.",
 * valor em JSON) para que o interceptor e o restante da aplicacao continuem lendo o mesmo lugar;
 * o refresh e o id token ficam no sessionStorage.
 */

const KEYCLOAK_PKCE_VERIFIER = "kc.pkceVerifier";
const KEYCLOAK_STATE = "kc.state";
const KEYCLOAK_REFRESH_TOKEN = "kc.refreshToken";
const KEYCLOAK_ID_TOKEN = "kc.idToken";
const KEYCLOAK_LOGIN_REDIRECTS = "kc.loginRedirects";
const KEYCLOAK_TOKEN_RECUSADO = "kc.tokenRecusado";
const LS_TOKEN = "ls.token";
const LS_USER = "ls.user";
const LS_AUTH_PROVIDER = "ls.authProvider";
const KEYCLOAK_PROVIDER = "keycloak";

/** Renova o access token quando faltar menos que isto (segundos) para expirar. */
const MARGEM_RENOVACAO_SEGUNDOS = 60;

/**
 * Redirects to the Keycloak login a tab may make within JANELA_REDIRECIONAMENTOS_MS before it stops and
 * shows the login error instead. A healthy sign-in makes one; the re-login loop made one every ~4 s.
 */
const MAX_REDIRECIONAMENTOS_LOGIN = 3;
const JANELA_REDIRECIONAMENTOS_MS = 60 * 1000;

interface KeycloakConfig {
  enabled?: any;
  url?: string;
  realm?: string;
  clientId?: string;
}

function config(): KeycloakConfig {
  return (environment as any).keycloak || {};
}

/** A flag vem como texto do pipeline ("true"/"false"); o placeholder nao substituido conta como desligada. */
export function isKeycloakEnabled(): boolean {
  return String(config().enabled) === "true";
}

function realmUrl(): string {
  return `${config().url}/realms/${config().realm}`;
}

// ---------------------------------------------------------------- localStorage
// O angular-local-storage grava JSON sob o prefixo "ls.". Replicamos a codificacao para ler/gravar
// sem depender do servico Angular (o interceptor roda antes de qualquer injecao).

function lsGet(chave: string): any {
  const bruto = window.localStorage.getItem(chave);
  if (!bruto || bruto === "null") {
    return null;
  }
  try {
    return JSON.parse(bruto);
  } catch (e) {
    return bruto;
  }
}

function lsSet(chave: string, valor: any): void {
  window.localStorage.setItem(chave, JSON.stringify(valor));
}

/** A sessao atual e do Keycloak (e nao do login legado)? */
export function isKeycloakSession(): boolean {
  return isKeycloakEnabled() && lsGet(LS_AUTH_PROVIDER) === KEYCLOAK_PROVIDER;
}

export function getIdToken(): string | null {
  return window.sessionStorage.getItem(KEYCLOAK_ID_TOKEN);
}

/** Remove refresh e id token, e o marcador do provedor. Chamar DEPOIS de ler o id token, se for usa-lo. */
export function limparSessaoKeycloak(): void {
  window.sessionStorage.removeItem(KEYCLOAK_REFRESH_TOKEN);
  window.sessionStorage.removeItem(KEYCLOAK_ID_TOKEN);
  window.sessionStorage.removeItem(KEYCLOAK_PKCE_VERIFIER);
  window.sessionStorage.removeItem(KEYCLOAK_STATE);
  window.sessionStorage.removeItem(KEYCLOAK_TOKEN_RECUSADO);
  window.localStorage.removeItem(LS_AUTH_PROVIDER);
}

/**
 * This tab gave up on the stored access token (a 401, or a renewal that failed) and is signing in again.
 * The token stays in localStorage for the other tabs, which may still renew it with their own refresh
 * token; here it counts as no session until another one is stored, by this tab or by any other.
 */
export function tokenRecusadoNestaAba(): boolean {
  const bruto = window.localStorage.getItem(LS_TOKEN);
  return !!bruto && bruto === window.sessionStorage.getItem(KEYCLOAK_TOKEN_RECUSADO);
}

/** A session this tab can use: a stored token it has not given up on. */
export function temSessaoNestaAba(): boolean {
  return !!lsGet(LS_TOKEN) && !tokenRecusadoNestaAba();
}

/**
 * Ends the Keycloak session in this tab only: forgets its refresh and id token and its sign-in state, and
 * gives up on the stored access token here (tokenRecusadoNestaAba). ls.token, ls.user and the provider
 * marker are shared by every tab and stay; wiping them logged the other tabs out too, even those that
 * could still renew the token.
 */
export function encerrarSessaoNestaAba(): void {
  const bruto = window.localStorage.getItem(LS_TOKEN);
  window.sessionStorage.removeItem(KEYCLOAK_REFRESH_TOKEN);
  window.sessionStorage.removeItem(KEYCLOAK_ID_TOKEN);
  window.sessionStorage.removeItem(KEYCLOAK_PKCE_VERIFIER);
  window.sessionStorage.removeItem(KEYCLOAK_STATE);
  if (bruto) {
    window.sessionStorage.setItem(KEYCLOAK_TOKEN_RECUSADO, bruto);
  }
}

/**
 * A different account signed in on this browser: drops what the previous one left in localStorage (its
 * user, public API token, last addresses and order...), keeping the new session and the preserved items.
 * A 401 no longer wipes the storage, since other tabs may still be using it, so it is done here.
 */
export function esquecerDadosDeOutraConta(): void {
  const preservados = preserveItems();
  Object.keys(window.localStorage)
    .filter(
      (chave) =>
        chave.indexOf("ls.") === 0 &&
        chave !== LS_TOKEN &&
        chave !== LS_AUTH_PROVIDER
    )
    .forEach((chave) => window.localStorage.removeItem(chave));
  restorePreserved(preservados);
}

/** The e-mail stored for the previous sign-in and the one just signed in are the same account. */
export function mesmaConta(emailAnterior: any, emailNovo: any): boolean {
  const normalizar = (email: any) => String(email || "").trim().toLowerCase();
  return !!normalizar(emailAnterior) && normalizar(emailAnterior) === normalizar(emailNovo);
}

/** Drops what a half-finished sign-in leaves behind: access token, user and the Keycloak markers. */
export function limparSessaoLocal(): void {
  limparSessaoKeycloak();
  window.localStorage.removeItem(LS_TOKEN);
  window.localStorage.removeItem(LS_USER);
}

// ------------------------------------------------------------------------ PKCE

function base64UrlEncode(bytes: Uint8Array): string {
  let binario = "";
  for (let i = 0; i < bytes.length; i++) {
    binario += String.fromCharCode(bytes[i]);
  }
  return window
    .btoa(binario)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function aleatorio(tamanho: number): string {
  const bytes = new Uint8Array(tamanho);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(valor: string): Promise<Uint8Array> {
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new (window as any).TextEncoder().encode(valor)
  );
  return new Uint8Array(digest);
}

/** Onde o Keycloak devolve o usuario depois do login: a propria /login, que consome o code. */
export function redirectUriDeLogin(): string {
  return window.location.origin + "/login";
}

/** URL da tela de login do Keycloak; guarda o verifier e o state para a volta. */
export async function montarUrlDeLogin(redirectUri: string): Promise<string> {
  const verifier = aleatorio(64);
  const state = aleatorio(16);
  const challenge = base64UrlEncode(await sha256(verifier));

  window.sessionStorage.setItem(KEYCLOAK_PKCE_VERIFIER, verifier);
  window.sessionStorage.setItem(KEYCLOAK_STATE, state);

  const params = new URLSearchParams();
  params.set("client_id", String(config().clientId));
  params.set("response_type", "code");
  params.set("scope", "openid profile email");
  params.set("redirect_uri", redirectUri);
  params.set("state", state);
  params.set("code_challenge", challenge);
  params.set("code_challenge_method", "S256");

  return `${realmUrl()}/protocol/openid-connect/auth?${params.toString()}`;
}

function redirecionamentosRecentes(agora: number): number[] {
  try {
    const guardados = JSON.parse(
      window.sessionStorage.getItem(KEYCLOAK_LOGIN_REDIRECTS) || "[]"
    );
    return (Array.isArray(guardados) ? guardados : []).filter(
      (t) => typeof t === "number" && agora - t < JANELA_REDIRECIONAMENTOS_MS
    );
  } catch (e) {
    return [];
  }
}

/**
 * Counts one more redirect to the Keycloak login for this tab. Returns false (without counting) once the
 * tab already made MAX_REDIRECIONAMENTOS_LOGIN of them within the window: a session that is dropped right
 * after a successful sign-in sends the user back here with the SSO cookie alive, which signs them in
 * again, and so on. Without storage there is nothing to count with, so the redirect is allowed.
 */
export function registrarRedirecionamentoDeLogin(
  agora: number = Date.now()
): boolean {
  const recentes = redirecionamentosRecentes(agora);
  if (recentes.length >= MAX_REDIRECIONAMENTOS_LOGIN) {
    return false;
  }
  try {
    recentes.push(agora);
    window.sessionStorage.setItem(
      KEYCLOAK_LOGIN_REDIRECTS,
      JSON.stringify(recentes)
    );
  } catch (e) {}
  return true;
}

/** The user asked to try again: forget the redirects counted so far. */
export function reiniciarContagemDeLogin(): void {
  try {
    window.sessionStorage.removeItem(KEYCLOAK_LOGIN_REDIRECTS);
  } catch (e) {}
}

/** Rejects (with laco = true) instead of redirecting when the tab is caught in a re-login loop. */
export async function irParaLogin(): Promise<void> {
  if (!registrarRedirecionamentoDeLogin()) {
    throw Object.assign(
      new Error("Muitos redirecionamentos ao login do Keycloak em pouco tempo"),
      { laco: true }
    );
  }
  window.location.assign(await montarUrlDeLogin(redirectUriDeLogin()));
}

/** A URL atual traz o retorno de um login do Keycloak? */
export function temRetornoDeLogin(search: string): boolean {
  return /[?&]code=/.test(search) && /[?&]state=/.test(search);
}

/** This page is the Keycloak return (?code=&state=): the sign-in is still being completed, there is no session yet. */
export function emRetornoDeLogin(
  search: string = window.location.search
): boolean {
  return isKeycloakEnabled() && temRetornoDeLogin(search);
}

function parametro(search: string, nome: string): string | null {
  const m = new RegExp("[?&]" + nome + "=([^&]*)").exec(search);
  return m ? decodeURIComponent(m[1].replace(/\+/g, " ")) : null;
}

async function pedirTokens(corpo: URLSearchParams): Promise<any> {
  const resposta = await fetch(`${realmUrl()}/protocol/openid-connect/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: corpo.toString(),
  });
  if (!resposta.ok) {
    throw new Error("Keycloak recusou o pedido de token: " + resposta.status);
  }
  return resposta.json();
}

function gravarTokens(tokens: any): void {
  lsSet(LS_TOKEN, tokens.access_token);
  lsSet(LS_AUTH_PROVIDER, KEYCLOAK_PROVIDER);
  window.sessionStorage.removeItem(KEYCLOAK_TOKEN_RECUSADO);
  if (tokens.refresh_token) {
    window.sessionStorage.setItem(KEYCLOAK_REFRESH_TOKEN, tokens.refresh_token);
  }
  if (tokens.id_token) {
    window.sessionStorage.setItem(KEYCLOAK_ID_TOKEN, tokens.id_token);
  }
}

/**
 * Consome o retorno do login: confere o state, troca o code por tokens (com o verifier guardado) e
 * grava o access token. Devolve os claims do access token.
 */
export async function concluirLogin(search: string): Promise<any> {
  const code = parametro(search, "code");
  const state = parametro(search, "state");
  const estadoGuardado = window.sessionStorage.getItem(KEYCLOAK_STATE);
  const verifier = window.sessionStorage.getItem(KEYCLOAK_PKCE_VERIFIER);
  window.sessionStorage.removeItem(KEYCLOAK_STATE);
  window.sessionStorage.removeItem(KEYCLOAK_PKCE_VERIFIER);

  if (!code || !state || !estadoGuardado || state !== estadoGuardado) {
    throw new Error("Retorno do Keycloak invalido (state diferente do enviado)");
  }
  if (!verifier) {
    throw new Error("PKCE verifier nao encontrado, reinicie o login");
  }

  const corpo = new URLSearchParams();
  corpo.set("grant_type", "authorization_code");
  corpo.set("client_id", String(config().clientId));
  corpo.set("code", code);
  corpo.set("redirect_uri", redirectUriDeLogin());
  corpo.set("code_verifier", verifier);

  const tokens = await pedirTokens(corpo);
  gravarTokens(tokens);
  return decodificarToken(tokens.access_token);
}

export function decodificarToken(token: string): any {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(window.atob(payload))));
  } catch (e) {
    return {};
  }
}

function segundosParaExpirar(token: string): number {
  const exp = decodificarToken(token).exp;
  return exp ? exp - Math.floor(Date.now() / 1000) : 0;
}

let renovacaoEmAndamento: Promise<string> | null = null;

/**
 * Devolve um access token com pelo menos MARGEM_RENOVACAO_SEGUNDOS de vida, renovando pelo
 * refresh token quando preciso. Chamadas paralelas dividem uma unica renovacao. Rejeita quando
 * nao ha sessao do Keycloak ou o refresh falha (sessao SSO encerrada).
 */
export function tokenValido(): Promise<string> {
  const atual: string | null = lsGet(LS_TOKEN);
  if (!atual) {
    return Promise.reject(new Error("sem sessao do Keycloak"));
  }
  if (tokenRecusadoNestaAba()) {
    return Promise.reject(new Error("sessao encerrada nesta aba"));
  }
  if (segundosParaExpirar(atual) > MARGEM_RENOVACAO_SEGUNDOS) {
    return Promise.resolve(atual);
  }

  const refresh = window.sessionStorage.getItem(KEYCLOAK_REFRESH_TOKEN);
  if (!refresh) {
    return Promise.reject(new Error("sem refresh token"));
  }
  if (!renovacaoEmAndamento) {
    const corpo = new URLSearchParams();
    corpo.set("grant_type", "refresh_token");
    corpo.set("client_id", String(config().clientId));
    corpo.set("refresh_token", refresh);

    renovacaoEmAndamento = pedirTokens(corpo)
      .then((tokens) => {
        gravarTokens(tokens);
        renovacaoEmAndamento = null;
        return tokens.access_token as string;
      })
      .catch((erro) => {
        renovacaoEmAndamento = null;
        throw erro;
      });
  }
  return renovacaoEmAndamento;
}

/**
 * The access token in ls.token is expired or about to be, and this tab has no refresh token to renew it.
 * ls.token is shared by every tab and outlives them, while the refresh token lives in this tab's
 * sessionStorage: a new tab, a reopened browser or a link opened from elsewhere starts without one, and
 * every call it makes with that token comes back 401. Also true when this tab already gave up on the
 * stored token (tokenRecusadoNestaAba) and no other tab has stored a new one since.
 */
export function sessaoVencidaNestaAba(): boolean {
  const atual: string | null = lsGet(LS_TOKEN);
  if (!isKeycloakSession() || !atual) {
    return false;
  }
  return (
    tokenRecusadoNestaAba() ||
    (segundosParaExpirar(atual) <= MARGEM_RENOVACAO_SEGUNDOS &&
      !window.sessionStorage.getItem(KEYCLOAK_REFRESH_TOKEN))
  );
}

/**
 * authorize() may fail for the stored session: a legacy one is always refused, and a Keycloak one whose
 * access token is expired or about to be depends on a renewal that Keycloak can refuse (SSO session idle
 * or ended) or that this tab cannot make (no refresh token). A fresh Keycloak token never fails.
 */
export function sessaoPodeTerTerminado(): boolean {
  const atual: string | null = lsGet(LS_TOKEN);
  if (!isKeycloakEnabled() || !atual) {
    return false;
  }
  return (
    !isKeycloakSession() || segundosParaExpirar(atual) <= MARGEM_RENOVACAO_SEGUNDOS
  );
}

/** /login itself, or the Keycloak return on it: the sign-in is happening here, nothing to send elsewhere. */
export function naTelaDeLogin(): boolean {
  return window.location.pathname.endsWith("/login") || emRetornoDeLogin();
}

/**
 * Keeps the current page in ls.returnUrl for after the sign-in. The login only honours the pages that
 * may come back (login.controller.ts destinoPosLogin) and drops the rest.
 */
function guardarDestinoPosLogin(): void {
  try {
    window.localStorage.setItem("ls.returnUrl", window.location.pathname);
  } catch (e) {}
}

/**
 * Sends the browser to /login, which goes straight on to Keycloak, after the session in this tab ended.
 * Returns false, without leaving, when Keycloak is off or the page is already the sign-in.
 */
export function irParaTelaDeLogin(
  navegador: { href: string } = window.location
): boolean {
  if (!isKeycloakEnabled() || naTelaDeLogin()) {
    return false;
  }
  guardarDestinoPosLogin();
  navegador.href = "/login";
  return true;
}

/**
 * Run before the app boots. With a session this tab cannot renew (sessaoVencidaNestaAba), the app would
 * load the page and get 401 from every call; instead go straight to the Keycloak login, which signs the
 * user in again without the password while the SSO session is alive. The /login page takes over when the
 * re-login brake refuses the redirect. Returns true when the page is being left, so the caller does not
 * bootstrap the app.
 */
export function entrarDeNovoSeSessaoVencida(
  entrar: () => Promise<void> = irParaLogin,
  navegador: { replace(url: string): void } = window.location
): boolean {
  if (!sessaoVencidaNestaAba() || naTelaDeLogin()) {
    return false;
  }
  guardarDestinoPosLogin();
  entrar().catch(() => navegador.replace("/login"));
  return true;
}

/** URL de logout do Keycloak (encerra a sessao SSO) com volta para a /login. */
export function urlDeLogout(idToken: string | null): string {
  const params = new URLSearchParams();
  params.set("client_id", String(config().clientId));
  params.set("post_logout_redirect_uri", window.location.origin + "/login");
  if (idToken) {
    params.set("id_token_hint", idToken);
  }
  return `${realmUrl()}/protocol/openid-connect/logout?${params.toString()}`;
}

/**
 * Console de conta do Keycloak, que abre em "Seguranca e acesso" (troca de senha e verificacao em duas
 * etapas). O referrer faz o console mostrar "Voltar para <nome do client>" apontando para referrer_uri.
 */
export function urlDoConsoleDeConta(): string {
  const params = new URLSearchParams();
  params.set("referrer", String(config().clientId));
  params.set("referrer_uri", window.location.origin + "/");
  return `${realmUrl()}/account/?${params.toString()}`;
}

/** Na mesma aba: o refresh token fica no sessionStorage desta aba, e o "Voltar" do console retoma a sessao. */
export function irParaConsoleDeConta(): void {
  window.location.assign(urlDoConsoleDeConta());
}

/** O header Authorization de uma sessao do Keycloak leva o esquema Bearer. */
export function comEsquemaBearer(token: string): string {
  return token.indexOf("Bearer ") === 0 ? token : "Bearer " + token;
}
